import { formatCurrency, formatNumber } from '@angular/common';
import { Directive, ElementRef, HostListener, Inject, Input, LOCALE_ID, OnInit } from '@angular/core';
import { NgControl } from '@angular/forms';

@Directive({
    selector: '[libInputFormatter]'
})
export class InputFormatterDirective implements OnInit {
    format = 'N0';
    digitsInfo = '1.0-0';
    @Input() currency = '$';
    @Input() prefix = '';
    @Input() suffix = '';
    @Input() decimalCharacter = null;
    @Input('libInputFormatter') set _(value: string) {
        this.format = value;
        if (this.format == 'N2') this.digitsInfo = '1.2-2';
        const parts = value.split(':');
        if (parts.length > 1) {
            this.format = parts[0];
            this.digitsInfo = parts[1];
        }
    }
    @HostListener('blur', ['$event.target']) blur(target: any) {
        if (target.value) target.value = this.parse(target.value);
    }
    @HostListener('focus', ['$event.target']) focus(target: any) {
        if (target.value) target.value = this.control.value;
    }

    @HostListener('input', ['$event.target']) onInput(target: any) {
        if (target.value) {
            let inputValue = target.value.replace(/,/g, '.');

            inputValue = inputValue.replace(/[^0-9.]/g, '');

            const firstDotIndex = inputValue.indexOf('.');

            if (target.value.length === 1 && firstDotIndex === 0) {
                inputValue = '';
            } else if (firstDotIndex !== -1) {
                const beforeDot = inputValue.slice(0, firstDotIndex + 1);
                const afterDot = inputValue.slice(firstDotIndex + 1).replace(/\./g, '');
                inputValue = beforeDot + afterDot;
            }

            target.value = inputValue;
            this.control.control?.setValue(inputValue);
        }
    }

    @HostListener('keypress', ['$event'])
    onKeypress(event: KeyboardEvent) {
        const input = this.el.nativeElement;
        if (event.key === '.' && input.value.includes('.')) {
            event.preventDefault();
        }
    }

    constructor(
        @Inject(LOCALE_ID) private readonly locale: string,
        private readonly el: ElementRef,
        private readonly control: NgControl
    ) {}

    ngOnInit() {
        setTimeout(() => {
            if (this.el.nativeElement.value) this.el.nativeElement.value = this.parse(this.el.nativeElement.value);
        });
    }

    /**
     * ### Standard Numeric Format Strings
     * Formats the input value based on the specified format and adds a suffix if provided
     * Examples of formats:
     * 'C2' - Currency with two decimal places (e.g., 1234.56 => '$1,234.56')
     * 'N2' - Numeric with two decimal places (e.g., 1234.56 => '1,234.56')
     * 'N0' - Numeric with no decimal places (e.g., 1234 => '1,234')
     * 'NX' - Custom numeric format (e.g., 123456 => '123,456') depending on digitsInfo
     * Adds a suffix to the value if provided (e.g., 100 => '100.00%' if suffix = '%')
     * Adds a prefix to the value if provided (e.g., 100 => '@100.00' if prefix = '@')
     * Replaces the decimal separator (e.g., 1234.56 => '1.234,56' if decimalCharacter = ',')
     */
    parse(value: any) {
        if (value == null || value === '' || value === 'NaN') return '';

        let newValue = String(value);

        if (this.prefix) {
            newValue = newValue.replace(new RegExp(`^${this.prefix}`), '');
        }
        if (this.suffix) {
            const escapedSuffix = this.suffix.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
            newValue = newValue.replace(new RegExp(`${escapedSuffix}$`), '');
        }

        const numeric = Number(newValue.replace(/,/g, '.').replace(/[^0-9.]/g, ''));

        let formatted: string;
        switch (this.format) {
            case 'C2':
                formatted = formatCurrency(numeric, this.locale, this.currency);
                break;
            case 'N2':
            case 'N0':
            case 'NX':
                formatted = formatNumber(numeric, this.locale, this.digitsInfo);
                break;
            default:
                formatted = String(numeric);
        }

        if (this.decimalCharacter) {
            formatted = newValue.replace('.', 'x').replace(',', '.').replace('x', ',') + this.suffix;
        }

        return this.prefix + formatted + this.suffix;
    }

    /**
     * Public method to apply formatting programmatically
     * Useful when values are changed programmatically and need to be formatted
     */
    public applyFormat(): void {
        if (this.el.nativeElement.value) {
            this.el.nativeElement.value = this.parse(this.el.nativeElement.value);
        }
    }
}
